import { useCallback } from 'react';
import { IBonusConfig } from 'js/components/system/App/store/types';
import { useNavigate } from 'js/hooks/navigate';
import { useLaunchGame } from 'js/hooks/gameLauncher';
import { useLoginPopup } from 'js/components/popups/LoginPopup';
import { useAppSelector, useUserSelector } from 'js/store/hooks';
import { path, pick, prop } from 'ramda';
import { htcmdService } from 'js/services/htcmd.service';
import { useBonusTrigger, useOptInFromBonusTemplate, useOptOutFromBonusTemplate } from 'server';
import { useTranslations } from 'js/hooks/i18n';
import { useDispatch } from 'react-redux';
import { showCommonPopupAction } from 'js/components/common/popups/CommonPopup';
import { showBonusBuyInPopupAction } from 'js/components/popups/BonusBuyInPopup';
import { publicEventsAPIService } from 'js/services/publicEventsAPI.service';

export const useLogicButton = ({ bonus }: { bonus: IBonusConfig }) => {
    const navigate = useNavigate();
    const dispatch = useDispatch();
    const translate = useTranslations();
    const bonusTrigger = useBonusTrigger();
    const bonusFreeCode = useAppSelector(path(['bonusManagementPreferences', 'bonusFreeCode']));
    const optInFromBonusTemplate = useOptInFromBonusTemplate();
    const optOutFromBonusTemplate = useOptOutFromBonusTemplate();
    const isLoggedIn = useUserSelector(prop('isLoggedIn'));
    const launchGame = useLaunchGame();
    const showLoginPopup = useLoginPopup();
    const login = useCallback(({ onLogin }: { onLogin: () => void }) => {
        if (htcmdService.isEnabled || htcmdService.isWrapperEnabled) {
            htcmdService.openLogin();
        } else {
            showLoginPopup({
                onLogin
            });
        }
    }, [
        showLoginPopup
    ]);
    const {
        registrationURL,
        depositPage
    } = useAppSelector(pick([
        'registrationURL',
        'depositPage'
    ]));

    const showBuyInBonusOverlay = useCallback(() => {
        dispatch(showBonusBuyInPopupAction({
            title: /* bonus title */ 'title',
            buyInWebContentId: bonus.buyInWebContentId,
            bonusCode: bonus.bonusCode,
            termAndConditionWebContentId: bonus.termAndConditionWebContentId
        }));
    }, [
        dispatch,
        bonus
    ]);

    const getFreeBonus = useCallback(async () => {
        const { result } = await bonusTrigger({
            templateCode: bonus.bonusCode,
            triggerType: 'PromotionCode',
            promotionCode: String(bonusFreeCode ?? '')
        });

        if ([2, 3].includes(result)) {
            dispatch(showCommonPopupAction({
                content: translate(`playtech.mobile.system.getFreeBonus.error.${result}`)
            }));
        }
    }, [
        bonusTrigger,
        bonus,
        bonusFreeCode,
        dispatch,
        translate
    ]);

    const optIn = useCallback(async () => {
        await optInFromBonusTemplate({ templateCode: bonus.bonusCode });

        publicEventsAPIService.trigger('promotion-opt-in', { bonus });

        const content = [
            translate('playtech.mobile.system.optIn.success'),
            translate(`playtech.mobile.system.bonus.${bonus.bonusCode}`)
        ];

        dispatch(showCommonPopupAction(bonus.successOptInWebContentId
            ? { articleId: bonus.successOptInWebContentId }
            : { content: content.join(' ') }));
    }, [
        dispatch,
        optInFromBonusTemplate,
        translate,
        bonus
    ]);

    const optOut = useCallback(async () => {
        await optOutFromBonusTemplate({ templateCode: bonus.bonusCode });

        publicEventsAPIService.trigger('promotion-opt-out', { bonus });

        const content = [
            translate('playtech.mobile.system.optOut.success'),
            translate(`playtech.mobile.system.bonus.${bonus.bonusCode}`)
        ];

        dispatch(showCommonPopupAction(bonus.successOptInWebContentId
            ? { articleId: bonus.successOptInWebContentId }
            : { content: content.join(' ') }));
    }, [
        dispatch,
        optOutFromBonusTemplate,
        translate,
        bonus
    ]);

    return useCallback(() => {
        if (!bonus) {
            return;
        }

        switch (bonus.bonusTrigger) {
            case 'FREE': {
                if (isLoggedIn) {
                    getFreeBonus();
                } else {
                    login({ onLogin: getFreeBonus });
                }

                break;
            }

            case 'OPT_IN': {
                if (isLoggedIn) {
                    optIn();
                } else {
                    login({ onLogin: optIn });
                }

                break;
            }

            case 'OPT_OUT': {
                if (isLoggedIn) {
                    optOut();
                } else {
                    login({ onLogin: optOut });
                }

                break;
            }

            case 'BUY_IN': {
                if (isLoggedIn) {
                    showBuyInBonusOverlay();
                } else {
                    login({ onLogin: showBuyInBonusOverlay });
                }
                break;
            }

            case 'LOGIN':
                showLoginPopup();
                break;

            case 'SIGN_UP':
                navigate(registrationURL);
                break;

            case 'DEPOSIT':
                navigate(depositPage);
                break;

            case 'CUSTOM':
            case 'CAMPAIGN': {
                if (bonus.bonusTrigger === 'CAMPAIGN') {
                    const controller = new AbortController();

                    publicEventsAPIService.trigger('promotion-campaign', {
                        bonus,
                        controller
                    });

                    if (controller.signal.aborted) {
                        return;
                    }
                }

                const {
                    bonusAction,
                    bonusActionGameCode,
                    bonusActionUrl
                } = bonus;

                switch (bonusAction) {
                    case 'GAME_CODE': {
                        launchGame({ code: bonusActionGameCode });
                        break;
                    }

                    case 'URL': {
                        const url = new URL(bonusActionUrl, document.baseURI);
                        const nativeCommand = url.searchParams.get('nativeCommand');
                        const nativeCommandGuest = url.searchParams.get('nativeCommandGuest');

                        url.searchParams.delete('nativeCommand');
                        url.searchParams.delete('nativeCommandGuest');

                        if ((htcmdService.isWrapperEnabled || htcmdService.isEnabled) && nativeCommand) {
                            const nativeUrl = new URL(!isLoggedIn && nativeCommandGuest ? nativeCommandGuest : nativeCommand);

                            htcmdService.sendMessage(nativeUrl.pathname, Object.fromEntries(nativeUrl.searchParams));
                        } else {
                            navigate(bonusActionUrl);
                        }

                        break;
                    }

                    default:
                    // noop
                }

                break;
            }
            default:
            // noop
        }
    }, [
        bonus,
        login,
        optIn,
        optOut,
        isLoggedIn,
        showBuyInBonusOverlay,
        showLoginPopup,
        depositPage,
        getFreeBonus,
        registrationURL,
        launchGame,
        navigate
    ]);
};
